import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome, FaChevronRight } from 'react-icons/fa';
import { SALES_DATA_ROLES } from '../../constants/activationRoles';

/**
 * Route-to-breadcrumb configuration
 * Maps route paths to their display labels and parent groups
 */
/**
 * Parent group → default landing route
 */
const GROUP_LINKS = {
  'Master Data': '/brands',
  'Price Management': '/price-structures',
  'Stock Management': '/inventory',
  // Group names mirror the sidebar's group titles so the breadcrumb parent and
  // the nav agree. 'Business Plan' (not 'Saved Plans', which is a leaf page) and
  // the Budget Projections landing points at the live ProjectionV2 page — the
  // legacy /budget-projections module is frozen.
  'Business Plan': '/business-plans',
  'Budget Projections': '/projections-v2',
  'Marketing': '/marketing/categories',
};

/**
 * Group landing pages that some roles shown the group cannot open. A role outside `roles`
 * gets `fallback`, a page of the group that every role shown the group can open.
 */
const GROUP_LINK_ROLES = {
  'Stock Management': { roles: SALES_DATA_ROLES, fallback: '/purchase-orders' },
  'Budget Projections': { roles: SALES_DATA_ROLES, fallback: '/brand-targets' },
  // /marketing/categories admits admin and manager (App.js); the sidebar shows Marketing
  // to brand_manager and the activation team roles too, and Activation admits them all.
  'Marketing': { roles: ['admin', 'manager'], fallback: '/marketing/activations' },
};

const groupLink = (group, hasRole) => {
  const gate = GROUP_LINK_ROLES[group];
  if (gate && !(hasRole && hasRole(...gate.roles))) return gate.fallback;
  return GROUP_LINKS[group];
};

// Labels mirror the sidebar's titles so the breadcrumb and nav agree. Parent
// groups match the sidebar's group headings (see GROUP_LINKS). Every navigable,
// in-nav route has an entry; :id detail routes fall back to their parent path
// via the segment-stripping scan in the component.
const ROUTE_CONFIG = {
  '/': { label: 'Dashboard' },
  // No parent group: the inbox belongs to the person, not to a module.
  '/notifications': { label: 'Notifications' },
  '/calculator': { label: 'Price Calculator', parent: 'Price Management' },

  // Master Data
  '/brands': { label: 'Brands', parent: 'Master Data' },
  '/suppliers': { label: 'Suppliers', parent: 'Master Data' },
  '/currencies': { label: 'Currencies', parent: 'Master Data' },
  '/markets': { label: 'Markets', parent: 'Master Data' },
  '/segments': { label: 'Door Segments', parent: 'Master Data' },
  '/doors': { label: 'Doors', parent: 'Master Data' },
  '/door-sales': { label: 'Door Sales', parent: 'Master Data' },
  '/door-channels': { label: 'Door Channels', parent: 'Master Data' },
  '/outpost-sales': { label: 'Outpost Sales', parent: 'Master Data' },
  '/data-consistency': { label: 'Sales Data Consistency Check', parent: 'Master Data' },
  '/brand-door-weights': { label: 'Door Weights', parent: 'Master Data' },
  '/ba-setup': { label: 'BA Setup', parent: 'Master Data' },
  '/beauty-advisor-sales': { label: 'Beauty Advisor Sales', parent: 'Master Data' },
  '/advisor-sales-export': { label: 'Beauty Advisor Sales Export', parent: 'Master Data' },
  '/pricing-channels': { label: 'Pricing Channels', parent: 'Master Data' },
  '/brand-groups': { label: 'Reporting Groups', parent: 'Master Data' },
  '/supplier-report-templates': { label: 'Supplier Report Templates', parent: 'Master Data' },
  '/master-data/purchase-orders': { label: 'Purchase Orders', parent: 'Master Data' },
  '/master-data/seasonality-calendar': { label: 'Seasonality Calendar', parent: 'Master Data' },
  '/users': { label: 'Users', parent: 'Master Data' },

  // Price Management
  '/price-structures': { label: 'Price Structures', parent: 'Price Management' },
  '/brand-list-mrp': { label: 'Brand List MRP', parent: 'Price Management' },
  '/price-lists': { label: 'Brand List MRP', parent: 'Price Management' }, // legacy path alias
  // The queue is reached from Brand List MRP and worked against it, so the
  // crumb has to lead back there — 'Price Management' lands on Price
  // Structures, which is not where the reader came from.
  '/brand-list-mrp/unconfirmed': {
    label: 'Unconfirmed MRPs',
    parent: 'Price Management',
    listParent: { label: 'Brand List MRP', path: '/brand-list-mrp' },
  },
  '/templates': { label: 'Templates', parent: 'Price Management' },
  '/budgets': { label: 'Brand Budgets', parent: 'Price Management' },

  // Stock Management
  '/products': { label: 'Product Catalog', parent: 'Stock Management' },
  '/inventory': { label: 'Stock & Inventory', parent: 'Stock Management' },
  '/arrival-pipeline': { label: 'Arrival Pipeline', parent: 'Stock Management' },
  '/arrival-pipeline/novelties': { label: 'Novelty Pipeline', parent: 'Marketing' },
  '/inventory-timeline': { label: 'Inventory Timeline', parent: 'Stock Management' },
  '/shopify-stock': { label: 'Shopify Stock', parent: 'Stock Management' },
  '/inventree-parts': { label: 'InvenTree Parts List', parent: 'Stock Management' },
  '/purchase-orders': { label: 'Purchase Orders', parent: 'Stock Management' },
  '/shipment-timeline': { label: 'Shipment Timeline', parent: 'Stock Management' },

  // Analytics (top-level, no group)
  '/sales-reporting': { label: 'Sales Reporting' },
  '/purchase-bp-dashboard': { label: 'Purchase & BP Dashboard' },
  '/api-keys': { label: 'API Keys' },

  // GA Management
  '/ga-data': { label: 'GA Review', parent: 'GA Management' },
  '/ga-new-po': { label: 'GA PO Selection', parent: 'GA Management' },

  // Business Plan
  '/business-plans': { label: 'Saved Plans', parent: 'Business Plan' },
  '/business-plan': { label: 'Plan Editor', parent: 'Business Plan' },
  '/existing-brand-business-plans': { label: 'Existing Brand Business Plan', parent: 'Business Plan' },
  '/existing-brand-business-plan': { label: 'Existing Brand Business Plan', parent: 'Business Plan' },
  '/business-plan/ai-assistant': { label: 'AI Assistant', parent: 'Business Plan', listParent: { label: 'Plan Editor', path: '/business-plan' } },
  // Both sit under /business-plan/…, so without their own entries the
  // segment-stripping scan would fall through to '/business-plan' and label them
  // "Plan Editor". The list is the module's landing page, so it takes the module
  // name and the editor hangs off it.
  '/business-plan/product-line-extrapolations': { label: 'Product Line Extrapolator', parent: 'Business Plan' },
  '/business-plan/product-line-extrapolator': {
    label: 'Extrapolation',
    parent: 'Business Plan',
    listParent: { label: 'Product Line Extrapolator', path: '/business-plan/product-line-extrapolations' }
  },
  '/sob-matrix': { label: 'SOB Matrix', parent: 'Business Plan' },
  '/email-templates': { label: 'Email Templates', parent: 'Business Plan' },

  // Budget Projections
  '/projections-v2': { label: 'Projections', parent: 'Budget Projections' },
  '/budget-projections': { label: 'Projections (legacy)', parent: 'Budget Projections' },
  // Reached from Master Data → Product Sales. The Budget Projections and
  // Marketing menus link to the same page, but Master Data is where it is
  // named "Product Sales", so that is the crumb.
  '/budget-projections/product-sales': { label: 'Product Sales', parent: 'Master Data' },
  '/budget-projections/yoy-report': { label: 'Sales YoY Report', parent: 'Budget Projections' },
  '/brand-ranking': { label: 'Brand Ranking', parent: 'Budget Projections' },
  '/brand-targets': { label: 'Brand Targets', parent: 'Budget Projections' },

  // Marketing
  '/marketing/categories': { label: 'Categories', parent: 'Marketing' },
  '/marketing/items': { label: 'Items', parent: 'Marketing' },
  '/marketing/contractors': { label: 'Contractors', parent: 'Marketing' },
  '/marketing/expenses': { label: 'Expenses', parent: 'Marketing' },
  '/marketing/activations': { label: 'Activation', parent: 'Marketing' },
  '/marketing/processes': { label: 'Marketing Processes', parent: 'Master Data' },
  '/marketing/quotes': { label: 'Quotes', parent: 'Marketing' },
  '/marketing/invoices': { label: 'Invoices', parent: 'Marketing' },
  '/marketing/reports': { label: 'Reports', parent: 'Marketing' },
  '/capex': { label: 'CAPEX', parent: 'Marketing' },
};

/**
 * Breadcrumb Component
 * Auto-generates breadcrumbs based on the current route
 *
 * @param {Function} [props.hasRole] AuthContext's hasRole, which picks the group links in
 *   GROUP_LINK_ROLES. Without it every group link is one any role shown the group can open.
 */
const Breadcrumb = ({ hasRole } = {}) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Find matching route config (try exact match, then strip trailing segments for dynamic routes)
  let config = ROUTE_CONFIG[pathname];
  if (!config) {
    // Try parent path for dynamic routes like /business-plan/:id or /purchase-orders/:id
    const segments = pathname.split('/').filter(Boolean);
    while (segments.length > 0 && !config) {
      segments.pop();
      config = ROUTE_CONFIG['/' + segments.join('/')];
    }
  }

  // Don't render on dashboard or if no config found
  if (!config || pathname === '/') return null;

  // Pages can pass a dynamic label via location.state.breadcrumbLabel
  // e.g., the brand name on the Plan Editor page
  const dynamicLabel = location.state?.breadcrumbLabel;
  const parentLink = config.parent ? groupLink(config.parent, hasRole) : null;

  return (
    <nav aria-label="Breadcrumb" className="mb-3" style={{ paddingLeft: '1.5rem' }}>
      <ol className="d-flex align-items-center gap-1 list-unstyled mb-0" style={{ fontSize: '0.8rem' }}>
        <li>
          <Link
            to="/"
            className="d-flex align-items-center text-decoration-none"
            style={{ color: 'var(--text-muted)' }}
          >
            <FaHome style={{ fontSize: '0.75rem' }} />
          </Link>
        </li>
        {config.parent && (
          <>
            <li style={{ color: 'var(--text-muted)' }}>
              <FaChevronRight style={{ fontSize: '0.5rem' }} />
            </li>
            <li>
              {parentLink ? (
                <Link
                  to={parentLink}
                  className="text-decoration-none breadcrumb-link"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {config.parent}
                </Link>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>{config.parent}</span>
              )}
            </li>
          </>
        )}
        {config.listParent && (
          <>
            <li style={{ color: 'var(--text-muted)' }}>
              <FaChevronRight style={{ fontSize: '0.5rem' }} />
            </li>
            <li>
              <Link
                to={config.listParent.path}
                className="text-decoration-none breadcrumb-link"
                style={{ color: 'var(--text-muted)' }}
              >
                {config.listParent.label}
              </Link>
            </li>
          </>
        )}
        <li style={{ color: 'var(--text-muted)' }}>
          <FaChevronRight style={{ fontSize: '0.5rem' }} />
        </li>
        <li style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
          {config.label}
          {dynamicLabel && (
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.3rem' }}>
              — {dynamicLabel}
            </span>
          )}
        </li>
      </ol>
    </nav>
  );
};

export { Breadcrumb };
export default Breadcrumb;
